import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FormDialog } from "@/components/FormDialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { TechHeader } from "@/pages/tech/TechHome";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { movementOptions } from "@/lib/movements";
import { clientFields } from "@/lib/forms";

const NEW_CLIENT_FIELDS = clientFields.filter((f) => f.name !== "status");

const SEL = "h-12 w-full rounded-md border border-input bg-background px-3 text-base";
const EMPTY = { business_unit_code: "", service_type_code: "", service_address: "", contact_name: "", contact_phone: "", contact_email: "", scope: "" };

const F = ({ label, children, action }) => (
  <div className="space-y-1.5"><div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>{action}</div>{children}</div>
);

const ClientSearch = ({ client, onPick }) => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/clients/field-search", { params: { q } }).then((r) => setRows(r.data)).catch(() => setRows([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  if (client) return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-primary/40 bg-primary/10 p-3" data-testid="tech-new-wo-client-selected">
      <div><div className="font-semibold">{client.name}</div><div className="text-xs text-muted-foreground">{client.address}</div></div>
      <Button variant="ghost" size="sm" onClick={() => onPick(null)} data-testid="tech-new-wo-client-change">Cambiar</Button>
    </div>
  );
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground" />
        <Input className="h-12 pl-10 text-base" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, dirección o contacto" data-testid="tech-new-wo-client-search" />
      </div>
      <div className="max-h-64 space-y-1.5 overflow-y-auto" data-testid="tech-new-wo-client-results">
        {rows.map((c) => (
          <button key={c.id} type="button" onClick={() => onPick(c)} data-testid={`tech-new-wo-client-${c.id}`} className="block w-full rounded-lg border border-border p-3 text-left active:scale-[0.99]">
            <div className="font-medium">{c.name}</div>
            <div className="text-xs text-muted-foreground">{[c.address, c.contact_name].filter(Boolean).join(" · ")}</div>
          </button>
        ))}
        {rows.length === 0 && <p className="py-3 text-center text-sm text-muted-foreground">Sin coincidencias. Solo puedes elegir clientes existentes.</p>}
      </div>
    </div>
  );
};

export default function TechNewWorkOrder() {
  const { meta } = useAuth();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [newClient, setNewClient] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const pick = (c) => {
    setClient(c);
    if (c) setF((s) => ({ ...s, service_address: c.address || "", contact_name: c.contact_name || "", contact_phone: c.phone || "", contact_email: c.email || "" }));
  };
  const saveClient = async (form) => {
    const { data } = await api.post("/clients/field", form);
    pick(data);
    toast.success("Cliente creado");
  };
  const submit = async () => {
    if (!client || !f.business_unit_code || !f.service_type_code) return toast.error("Selecciona cliente, rubro y movimiento");
    setBusy(true);
    try {
      const { data } = await api.post("/work-orders", { ...f, client_id: client.id });
      toast.success(`OS ${data.folio} creada`);
      nav(`/tecnico/os/${data.id}`, { replace: true });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const moves = movementOptions(meta?.service_types, f.business_unit_code, f.service_type_code);

  return (
    <div className="mx-auto min-h-screen max-w-md pb-10">
      <TechHeader />
      <div className="space-y-5 px-4 pt-4" data-testid="tech-new-wo-form">
        <Link to="/tecnico" className="inline-flex items-center text-sm text-muted-foreground" data-testid="tech-new-wo-back"><ArrowLeft className="mr-1 h-4 w-4" />Mis órdenes</Link>
        <h1 className="font-display text-2xl font-bold">Nueva OS</h1>
        <F label="Cliente *" action={<Button type="button" size="icon" variant="outline" className="h-9 w-9" onClick={() => setNewClient(true)} aria-label="Nuevo cliente" data-testid="tech-new-wo-add-client-button"><Plus className="h-4 w-4" /></Button>}><ClientSearch client={client} onPick={pick} /></F>
        <FormDialog open={newClient} onOpenChange={setNewClient} title="Nuevo cliente" fields={NEW_CLIENT_FIELDS} onSubmit={saveClient} testid="tech-new-client" />
        <F label="Rubro *">
          <select className={SEL} value={f.business_unit_code} onChange={(e) => setF((s) => ({ ...s, business_unit_code: e.target.value, service_type_code: "" }))} data-testid="tech-new-wo-bu-select">
            <option value="">Seleccionar</option>
            {(meta?.business_units || []).map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
          </select>
        </F>
        <F label="Movimiento *">
          <select className={SEL} value={f.service_type_code} disabled={!f.business_unit_code} onChange={(e) => set("service_type_code", e.target.value)} data-testid="tech-new-wo-service-type-select">
            <option value="">{f.business_unit_code ? "Seleccionar" : "Primero elige rubro"}</option>
            {moves.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        </F>
        <F label="Dirección"><Input className="h-12 text-base" value={f.service_address} onChange={(e) => set("service_address", e.target.value)} data-testid="tech-new-wo-address-input" /></F>
        <F label="Contacto"><Input className="h-12 text-base" value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="tech-new-wo-contact-name-input" /></F>
        <F label="Teléfono"><Input className="h-12 text-base" type="tel" value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="tech-new-wo-contact-phone-input" /></F>
        <F label="Correo"><Input className="h-12 text-base" type="email" value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="tech-new-wo-contact-email-input" /></F>
        <F label="Alcance"><Textarea rows={3} className="text-base" value={f.scope} onChange={(e) => set("scope", e.target.value)} data-testid="tech-new-wo-scope-input" /></F>
        <Button className="h-14 w-full text-base" onClick={submit} disabled={busy} data-testid="tech-new-wo-submit-button">{busy && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}Crear OS</Button>
      </div>
    </div>
  );
}
